import { useEffect, useState } from 'react';
import NiceModal from "components/modals/NiceModal";
import { useMappedState } from 'redux-react-hook';
import { useSendFeedbackMutation } from 'services/api/serviceApi';
import styled from "styled-components";
import { errorAlert, successAlert } from 'ui-kit/Alert';

interface IFeedbackModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<IFeedbackModalProps> = (props) => {
  const { isVisible, onClose } = props;

  const [sendFeedback, { isLoading }] = useSendFeedbackMutation();
  const [message, setMessage] = useState('');

  const { currentUser } = useMappedState(state => {
    const currentUser = state.users.usersMap.get(state.users.currentUserId);
    return {
      currentUser: currentUser,
    };
  });


  const handleSendFeedback = async () => {
    if (!message) {
      errorAlert('Please write a message.');
      return;
    }
    if (isLoading) {
      return;
    }

    const formattedMessage = `${message}\nURL: ${window.location.href}`;

    try {
      await sendFeedback({
        message: formattedMessage,
      });
      onClose();
      successAlert('Thanks for your message!');
    } catch (error) {
      errorAlert('Failed to send feedback');
      throw error;
    }
  };

  useEffect(() => {
    return () => {
      setMessage('');
    };
  }, []);

  return (
    <NiceModal isVisible={isVisible} onClose={onClose}>
      <Root>
        <Title>Contact Us</Title>
        <Subtitle>
          <div>We appreciate your feedback.</div>
          {!currentUser && (
            <AttentionMessage>
              <b>Attention!</b> Your feedback is anonymous.
              <br />
              Indicate your email if you&apos;d like a response!
            </AttentionMessage>
          )}
        </Subtitle>
        <Wrapper className={'text-white'}>
          <div className="mt-4 grid grid-cols-1 gap-y-4" >
            <TextField
              name="message"
              required
              value={message}
              placeholder={'Write your message here'}
              onChange={e => setMessage(e.target.value)}
            />

            <div>
              <Button
                disabled={isLoading}
                className="w-full"
                onClick={handleSendFeedback}
              >
                Send
              </Button>
            </div>
          </div>
        </Wrapper>
      </Root>
    </NiceModal>
  );
};

export default FeedbackModal;

const Root = styled.div`
  width: 380px;
  padding: 24px;
  box-sizing: border-box;
  font-family: "Nunito Sans";
  font-optical-sizing: auto;
  
  @media (max-width: 410px) {
    width: unset;
  }
`;

const Wrapper = styled.div`
  font-family: 'Unbounded', sans-serif;
  font-optical-sizing: auto;
  width: 100%;
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 20px;
  text-align: center;
`;

const Subtitle = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: white;
  text-align: center;
`;

const AttentionMessage = styled.p`
  color: rgb(255, 126, 85);
`;

const TextField = styled.textarea`
  background-color: rgba(19, 19, 19, 1) !important;
  border: 1px solid transparent;
  border-radius: 8px;
  color: white;
  min-height: 128px;
  font-size: 16px;
  line-height: 18px;
  outline: none;
  font-family: Nunito Sans;
  padding: 10px;

  :focus {
    border-color: #ff7e55;
    box-shadow: none !important;
  }
`;

const Button = styled.div<{ disabled?: boolean }>`
  background: #ff7e55;
  border-radius: 1000px;
  color: white;
  font-weight: bold;
  padding-left: 15px;
  padding-right: 15px;
  height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  font-family: Nunito Sans;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  user-select: none;

  :hover {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }
`;

